/**
 * Map geodesy (phase 2b.6, docs/specs/map.md §3): projected survey coordinates to WGS 84 for the map.
 *
 * Planned lines, block boundaries and positions are stored in the block's or project's EPSG code (UTM / MGA
 * metres). The map draws WGS 84 degrees. `utmToLatLng` is the series inverse of `teslyn_core::planning::geo::
 * utm_to_latlon` (the Diurnal tool's `utmToLatLon`), in the same operation order, so the map, the planner and the
 * exports agree to 1e-9°. GDA94 and GDA2020 MGA zones use the same Transverse Mercator parameters on GRS 80, whose
 * flattening differs from WGS 84 by 1e-10: the inverse is the same to well under a millimetre; the datum shift
 * between GDA2020 and WGS 84 (under 2 m) is not applied, which is below what a map can show.
 *
 * A zone is never guessed (inventory 05 F17: the Diurnal 3D view defaulted to 55 S): an unknown EPSG gives null and
 * the layer reports the line as "not drawn: no map projection".
 */
import type { LatLng } from './planning.js';

export interface UtmZone {
  readonly zone: number;
  readonly hemisphere: 'N' | 'S';
}

/**
 * The UTM zone of a projected EPSG code: WGS 84 / UTM (326zz north, 327zz south), GDA94 / MGA (283zz, zones
 * 46–59), GDA2020 / MGA (78zz, zones 46–59), AGD66 / AMG (202zz, zones 48–58) and AGD84 / AMG (203zz, zones 48–58).
 * Null for anything else (the map does not guess).
 */
export function utmZoneOfEpsg(epsg: number | null | undefined): UtmZone | null {
  if (epsg === null || epsg === undefined || !Number.isInteger(epsg)) return null;
  const inRange = (z: number, lo: number, hi: number): boolean => z >= lo && z <= hi;
  if (epsg > 32600 && epsg <= 32660) return { zone: epsg - 32600, hemisphere: 'N' };
  if (epsg > 32700 && epsg <= 32760) return { zone: epsg - 32700, hemisphere: 'S' };
  if (inRange(epsg - 28300, 46, 59)) return { zone: epsg - 28300, hemisphere: 'S' };
  if (inRange(epsg - 7800, 46, 59)) return { zone: epsg - 7800, hemisphere: 'S' };
  if (inRange(epsg - 20200, 48, 58)) return { zone: epsg - 20200, hemisphere: 'S' };
  if (inRange(epsg - 20300, 48, 58)) return { zone: epsg - 20300, hemisphere: 'S' };
  return null;
}

/** UTM (WGS 84) to latitude and longitude, the series inverse of `utm_to_latlon` in teslyn-core (am:14903). */
export function utmToLatLng(easting: number, northing: number, zone: number, hemisphere: 'N' | 'S'): LatLng {
  const a = 6378137.0;
  const f = 1.0 / 298.257223563;
  const k0 = 0.9996;
  const e = Math.sqrt(2.0 * f - f * f);
  const e2 = e * e;
  const e1 = (1.0 - Math.sqrt(1.0 - e2)) / (1.0 + Math.sqrt(1.0 - e2));
  const ep2 = e2 / (1.0 - e2);

  const x = easting - 500000.0;
  const y = hemisphere === 'S' ? northing - 10000000.0 : northing;

  const m = y / k0;
  const mu = m / (a * (1.0 - e2 / 4.0 - (3.0 * e2 * e2) / 64.0 - (5.0 * e2 * e2 * e2) / 256.0));

  const phi1 =
    mu +
    ((3.0 * e1) / 2.0 - (27.0 * e1 * e1 * e1) / 32.0) * Math.sin(2.0 * mu) +
    ((21.0 * e1 * e1) / 16.0 - (55.0 * e1 * e1 * e1 * e1) / 32.0) * Math.sin(4.0 * mu) +
    ((151.0 * e1 * e1 * e1) / 96.0) * Math.sin(6.0 * mu) +
    ((1097.0 * e1 * e1 * e1 * e1) / 512.0) * Math.sin(8.0 * mu);

  const sinPhi1 = Math.sin(phi1);
  const n1 = a / Math.sqrt(1.0 - e2 * sinPhi1 * sinPhi1);
  const t1 = Math.tan(phi1) * Math.tan(phi1);
  const c1 = ep2 * Math.cos(phi1) * Math.cos(phi1);
  const r1 = (a * (1.0 - e2)) / Math.pow(1.0 - e2 * sinPhi1 * sinPhi1, 1.5);
  const d = x / (n1 * k0);

  const lat =
    phi1 -
    ((n1 * Math.tan(phi1)) / r1) *
      ((d * d) / 2.0 -
        ((5.0 + 3.0 * t1 + 10.0 * c1 - 4.0 * c1 * c1 - 9.0 * ep2) * d * d * d * d) / 24.0 +
        ((61.0 + 90.0 * t1 + 298.0 * c1 + 45.0 * t1 * t1 - 252.0 * ep2 - 3.0 * c1 * c1) *
          d *
          d *
          d *
          d *
          d *
          d) /
          720.0);

  const lon =
    (d -
      ((1.0 + 2.0 * t1 + c1) * d * d * d) / 6.0 +
      ((5.0 - 2.0 * c1 + 28.0 * t1 - 3.0 * c1 * c1 + 8.0 * ep2 + 24.0 * t1 * t1) * d * d * d * d * d) /
        120.0) /
    Math.cos(phi1);

  return { lat: (lat * 180.0) / Math.PI, lng: (lon * 180.0) / Math.PI + (zone * 6.0 - 183.0) };
}

/** A projected point in `epsg` as WGS 84, or null when the EPSG code is not a known UTM / MGA / AMG zone. */
export function projectedToLatLng(
  easting: number,
  northing: number,
  epsg: number | null | undefined,
): LatLng | null {
  const z = utmZoneOfEpsg(epsg);
  if (!z || !Number.isFinite(easting) || !Number.isFinite(northing)) return null;
  return utmToLatLng(easting, northing, z.zone, z.hemisphere);
}

/** A bounding box in degrees. */
export interface GeoBounds {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

/** The box around `points`, or null for none. */
export function boundsOf(points: readonly LatLng[]): GeoBounds | null {
  if (points.length === 0) return null;
  let south = Infinity;
  let west = Infinity;
  let north = -Infinity;
  let east = -Infinity;
  for (const p of points) {
    south = Math.min(south, p.lat);
    north = Math.max(north, p.lat);
    west = Math.min(west, p.lng);
    east = Math.max(east, p.lng);
  }
  return { south, west, north, east };
}

/** The union of boxes (nulls ignored). */
export function unionBounds(boxes: ReadonlyArray<GeoBounds | null>): GeoBounds | null {
  const real = boxes.filter((b): b is GeoBounds => b !== null);
  if (real.length === 0) return null;
  return {
    south: Math.min(...real.map((b) => b.south)),
    west: Math.min(...real.map((b) => b.west)),
    north: Math.max(...real.map((b) => b.north)),
    east: Math.max(...real.map((b) => b.east)),
  };
}

/** A box grown by `km` on every side (111.32 km per degree of latitude, as the hazard search area). */
export function expandBounds(b: GeoBounds, km: number): GeoBounds {
  const dLat = km / 111.32;
  const mid = (b.south + b.north) / 2;
  const dLng = km / (111.32 * Math.cos((mid * Math.PI) / 180));
  return {
    south: Math.max(-85, b.south - dLat),
    north: Math.min(85, b.north + dLat),
    west: Math.max(-180, b.west - dLng),
    east: Math.min(180, b.east + dLng),
  };
}

/**
 * True inside mainland Australia and Tasmania's box, the gate the Diurnal hazard pipeline uses (am:12799,
 * `in_australia` in teslyn-core); used to choose the elevation source (MB, Q68).
 */
export function inAustralia(p: LatLng): boolean {
  return p.lat >= -44 && p.lat <= -10 && p.lng >= 112 && p.lng <= 154;
}
